import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import { socketService } from '../../services/socket.service';

const REACTIONS = [
  { emoji: '\u{1F44D}', label: 'Thumbs Up' },
  { emoji: '\u{1F389}', label: 'Celebrate' },
  { emoji: '\u{2764}\u{FE0F}', label: 'Love' },
  { emoji: '\u{1F914}', label: 'Confused' },
  { emoji: '\u{270B}', label: 'Raise Hand' },
];

interface EmojiReactionBarProps {
  sessionId: string;
  currentStepIndex?: number;
}

const EmojiReactionBar: React.FC<EmojiReactionBarProps> = ({ sessionId, currentStepIndex }) => {
  const [cooldowns, setCooldowns] = useState<Record<string, boolean>>({});
  const [animating, setAnimating] = useState<string | null>(null);
  const cooldownTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  // M12: Clean up timers on unmount
  useEffect(() => {
    return () => {
      Object.values(cooldownTimers.current).forEach(clearTimeout);
    };
  }, []);

  const handleClick = useCallback((emoji: string) => {
    if (cooldowns[emoji]) return;

    socketService.sendReaction(sessionId, emoji, currentStepIndex);

    // Visual feedback animation
    setAnimating(emoji);
    setTimeout(() => setAnimating(null), 300);

    // 3-second cooldown
    setCooldowns((prev) => ({ ...prev, [emoji]: true }));
    cooldownTimers.current[emoji] = setTimeout(() => {
      setCooldowns((prev) => ({ ...prev, [emoji]: false }));
    }, 3000);
  }, [sessionId, currentStepIndex, cooldowns]);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.5,
        py: 0.75,
        px: 1.5,
        bgcolor: 'rgba(255,255,255,0.85)',
        backdropFilter: 'blur(8px)',
        borderRadius: '24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      {REACTIONS.map(({ emoji, label }) => (
        <Tooltip key={emoji} title={label} placement="top" arrow>
          <span>
            <IconButton
              size="small"
              onClick={() => handleClick(emoji)}
              disabled={cooldowns[emoji]}
              sx={{
                width: 40,
                height: 40,
                fontSize: 20,
                transition: 'transform 0.2s ease, opacity 0.2s ease',
                transform: animating === emoji ? 'scale(1.3)' : 'scale(1)',
                opacity: cooldowns[emoji] ? 0.4 : 1,
                '&:hover': {
                  bgcolor: 'rgba(42,123,136,0.08)',
                  transform: cooldowns[emoji] ? 'scale(1)' : 'scale(1.15)',
                },
              }}
            >
              {emoji}
            </IconButton>
          </span>
        </Tooltip>
      ))}
    </Box>
  );
};

export default EmojiReactionBar;
